<template>
  <ListComponent title="Container Type" :totalCount="totalItems">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="3" class="px-0 d-flex align-center">
          <v-text-field
            label="Search"
            class="search-field"
            dense
            outlined
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchContainerType"
          />
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFilterDialog = true"
                class="mx-2"
              >
                <v-badge
                  dot
                  color="primary"
                  :value="Object.keys(filters).length"
                >
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>
            <span v-if="Object.keys(filters).length == 0">Filters</span>

            <v-row v-else no-gutters>
              <v-col cols="12" class="font-weight-bold">Applied Filters</v-col>
              <v-col cols="12">
                <span
                  v-for="(value, key) in filters"
                  :key="key"
                  class="text-capitalize"
                  >{{ key.replace("_", " ") }}
                  <br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>
        </v-col>
        <v-col v-if="totalItems > 0" cols="9" class="px-0 d-flex justify-end">
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>

        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 192px)"
            class="ag-theme-alpine"
            id="myGrid"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="allContainerTypeList"
          />
        </v-col>
      </v-row>
    </template>

    <template #dialogs>
      <AdminContainerTypeFilters
        v-model="showFilterDialog"
        :bindingObject="filters"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(20)"
      />
      <ContainerTypeForm
        v-model="showContainerTypeForm"
        :editMode="isEditing"
        :editContainerTypeID="editContainerTypeID"
        @refreshList="itemsPerPageChanged(20)"
      />
    </template>

    <template #actions>
      <v-btn
        @click="addContainerType"
        color="primary"
        depressed
        v-if="permissions && permissions.add"
      >
        <v-icon>mdi-plus</v-icon>
        <span>Add Container Type</span>
      </v-btn>
    </template>
  </ListComponent>
</template>

<script>
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import ContainerTypeButton from "@/components/AgGridComponents/ContainerTypeButton.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import ContainerTypeForm from "@/components/ModuleBased/dialogForms/ContainerTypeForm.vue";
import AdminContainerTypeFilters from "@/components/ModuleBased/dialogs/filters/AdminContainerTypeFilters.vue";

import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    AgGridVue,
    Pagination,
    ContainerTypeButton,
    ContainerTypeForm,
    agColumnHeader: CustomHeader,
    AdminContainerTypeFilters,
  },
  data() {
    return {
      sorting: {},
      headers: [
        {
          headerName: "Type",
          field: "type_name",
          sortable: true,
        },
        {
          headerName: "Container Category",
          field: "container_category_name",
          sortable: true,
        },

        {
          headerName: "Size",
          field: "container_size",
          sortable: false,
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "ContainerTypeButton",
          pinned: "right",
          width: 100,
        },
      ],
      allContainerTypeList: [],
      showFilterDialog: false,

      // pagination vars
      itemsPerPage: 20,
      pageNo: 1,
      totalItems: 0,
      filters: {},

      search: "",
      permissions: null,

      // bulk vars
      showBulkDialog: false,

      // Container Type Form
      showContainerTypeForm: false,
      isEditing: false,
      editContainerTypeID: null,

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  methods: {
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(20);
    },
    searchContainerType() {
      this.pageNo = 1;
      this.getContainerTypeList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getContainerTypeList();
    },
    prevPage() {
      this.pageNo--;
      this.getContainerTypeList();
    },
    nextPage() {
      this.pageNo++;
      this.getContainerTypeList();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },
    getContainerTypeList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.containerType
        .getContainerTypeList(params)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.allContainerTypeList = res.data;
          this.totalItems = res.count;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    addContainerType() {
      this.editContainerTypeID = null;
      this.isEditing = false;
      this.showContainerTypeForm = true;
    },
    editContainerType(id) {
      this.editContainerTypeID = id;
      this.isEditing = true;
      this.showContainerTypeForm = true;
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      let url = localStorage.getItem("user_type").toLowerCase();
      if (url == "admin") {
        url = "admin_company";
      }
      this.$router.push(`/${url}`);
    } else {
      const permissions = getPermissions("lane");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        let url = localStorage.getItem("user_type").toLowerCase();
        if (url == "admin") {
          url = "admin_company";
        }
        this.$router.push(`/${url ? url : "error"}`);
      } else {
        this.permissions = { ...permissions };
        this.getContainerTypeList();
      }
    }
  },
};
</script>
